import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, X, Rocket, ArrowRight, Database, Trash2 } from "lucide-react";
import { toast } from "sonner";

const err = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;

export function useOnboarding() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/onboarding").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  return { data, reload: load, setData };
}

export function OnboardingPanel() {
  const { data, setData } = useOnboarding();
  if (!data || data.dismissed || data.complete) return null;
  const dismiss = async () => { const { data: d } = await api.post("/onboarding/dismiss"); setData(d); };
  return (
    <section className="mx-6 mt-6 border border-primary/30 bg-primary/5 rounded-lg p-5 relative overflow-hidden" data-testid="onboarding-panel">
      <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-primary/10 blur-2xl pointer-events-none" />
      <button onClick={dismiss} className="absolute right-3 top-3 text-muted-foreground hover:text-foreground transition-colors" aria-label="Dismiss" data-testid="onboarding-dismiss"><X className="w-4 h-4" /></button>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-md bg-primary/15 flex items-center justify-center shrink-0"><Rocket className="w-4.5 h-4.5 text-primary" /></div>
        <div>
          <div className="display text-base font-semibold tracking-tight">Get set up — {data.done} of {data.total} done</div>
          <div className="text-xs text-muted-foreground">Finish these four steps and your AI closer runs end to end: inbox → outreach → chat → booked calls.</div>
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-border/60 mb-4 overflow-hidden"><div className="h-full bg-primary transition-[width] duration-500" style={{ width: `${(data.done / data.total) * 100}%` }} data-testid="onboarding-progress" /></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {data.items.map((it) => (
          <Link key={it.key} to={it.href} className={`group border rounded-md p-3 flex flex-col gap-1.5 transition-[border-color,background-color] duration-150 ${it.done ? "border-emerald-500/30 bg-emerald-500/5" : "border-border bg-card hover:border-primary/50"}`} data-testid={`onboarding-item-${it.key}`}>
            <div className="flex items-center gap-2 text-sm font-medium">{it.done ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Circle className="w-4 h-4 text-muted-foreground" />}<span className={it.done ? "line-through text-muted-foreground" : ""}>{it.title}</span></div>
            <div className="text-[11px] text-muted-foreground leading-snug">{it.done && it.meta ? `Live on ${it.meta}` : it.hint}</div>
            {!it.done && <div className="text-[11px] text-primary mt-auto inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">Set up <ArrowRight className="w-3 h-3" /></div>}
          </Link>
        ))}
      </div>
    </section>
  );
}

export function OnboardingPill() {
  const { data } = useOnboarding();
  if (!data || data.complete || data.dismissed) return null;
  return (
    <Link to="/deals" className="mx-3 mb-1 flex items-center justify-between px-2.5 py-1.5 rounded-md border border-primary/30 bg-primary/5 text-[11px] hover:bg-primary/10 transition-colors" data-testid="onboarding-pill">
      <span className="flex items-center gap-1.5 text-foreground"><Rocket className="w-3 h-3 text-primary" />Setup</span>
      <span className="mono text-primary font-semibold">{data.done}/{data.total}</span>
    </Link>
  );
}

export function SampleDataControls() {
  const { data, reload } = useOnboarding();
  const [busy, setBusy] = useState(false);
  if (!data) return null;
  const run = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); await reload(); } catch (e) { toast.error(err(e)); } finally { setBusy(false); } };
  return (
    <section className="border border-border rounded-md bg-card p-5 flex items-center justify-between gap-3 flex-wrap" data-testid="sample-data-settings">
      <div>
        <div className="text-sm font-medium flex items-center gap-2"><Database className="w-4 h-4 text-muted-foreground" />Sample data</div>
        <div className="text-xs text-muted-foreground">{data.has_sample_data ? "Sample companies, contacts and deals are loaded in this workspace." : "Explore the app with 3 sample companies, 5 contacts, 5 deals and a starter sequence."}</div>
      </div>
      <div className="flex items-center gap-2">
        {data.has_sample_data
          ? <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => api.delete("/onboarding/sample-data"), "Sample data removed")} data-testid="sample-data-remove"><Trash2 className="w-3.5 h-3.5 mr-1.5" />Remove sample data</Button>
          : <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => api.post("/onboarding/sample-data"), "Sample data loaded")} data-testid="sample-data-load"><Database className="w-3.5 h-3.5 mr-1.5" />Load sample data</Button>}
        {data.dismissed && <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(() => api.post("/onboarding/restore"), "Setup checklist restored")} data-testid="onboarding-restore">Show setup checklist</Button>}
      </div>
    </section>
  );
}
